/**
 * Cross-product variant linking — click handling for the merged option matrix.
 *
 * Every pill is fully resolved server-side by snippets/linked-products-picker.liquid
 * (owner product, variant id, availability state), so this script only routes:
 *   - missing cell        → inert (value exists on no member)
 *   - unavailable cell    → soft jump (struck-out look, still clickable)
 *   - same-product cell   → drive the hidden native fieldsets so variant-picker.js
 *                           performs a normal no-reload variant switch
 *   - cross-product cell  → PDP: navigate to the owner with ?variant= preselected
 *                           Quick Buy: swap the modal to the owner product in place
 *
 * Loaded globally from scripts.liquid.
 */

(function () {
  const FLAG = '__linkedProductsPickerBoundV8';

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  function getLinkedMainInput(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    if (target instanceof HTMLInputElement && target.hasAttribute('data-linked-main-variant-input')) {
      return target;
    }

    const label = target.closest('label.variant-option__button-label, label');
    if (!label) return null;

    const input = label.querySelector('input[data-linked-main-variant-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {Element | null} el
   * @returns {boolean}
   */
  function isInQuickBuy(el) {
    if (!el) return false;
    if (el.closest('#quick-add-modal-content') || el.closest('.quick-add-modal__content')) return true;
    if (el.closest('quick-add-dialog')) return true;
    return Boolean(el.closest('dialog.quick-add-modal, .quick-add-modal'));
  }

  /**
   * Cross-product target URL: preserve the current query params except `variant`.
   * @param {string} productUrl
   * @param {string} variantId
   * @returns {string}
   */
  function buildTargetUrl(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('variant');
    currentParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });
    if (variantId) url.searchParams.set('variant', String(variantId));
    return url.toString();
  }

  /**
   * Reflect the clicked pill's checked state within its own row only.
   * The authoritative state is re-rendered server-side after the switch.
   * @param {HTMLInputElement} input
   */
  function syncRowChecked(input) {
    const row = input.closest('[data-linked-option-row], [data-linked-main-variants]');
    if (!row) return;
    row.querySelectorAll(`input[data-linked-main-variant-input][name="${CSS.escape(input.name)}"]`).forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const selected = radio === input;
      radio.checked = selected;
      radio.dataset.currentChecked = selected ? 'true' : 'false';
    });
  }

  /**
   * Announce the selection for screen readers.
   * @param {HTMLInputElement} input
   */
  function announce(input) {
    const scope = input.closest('variant-picker') || document;
    const live = scope.querySelector('[data-linked-live]');
    if (!(live instanceof HTMLElement)) return;
    let message = input.value || '';
    if (input.dataset.state === 'soldout') message += ' — sold out';
    live.textContent = message ? `${message} selected` : '';
  }

  /**
   * Same-product switch: check the hidden native option radios matching the
   * resolved variant's option values (product position order) and dispatch a
   * single change event so variant-picker.js runs its normal update flow.
   * @param {Element} variantPicker
   * @param {string[]} optionValues
   * @returns {boolean}
   */
  function driveNativePicker(variantPicker, optionValues) {
    const hidden = variantPicker.querySelector('.linked-products__hidden-options');
    if (!hidden) return false;

    /** @type {HTMLInputElement | null} */
    let lastChanged = null;

    optionValues.forEach((value, index) => {
      const fieldset = hidden.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      if (!fieldset) return;
      for (const radio of fieldset.querySelectorAll('input[type="radio"]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        if (radio.value === String(value)) {
          if (!radio.checked) {
            radio.checked = true;
            lastChanged = radio;
          }
          break;
        }
      }
    });

    if (lastChanged) {
      lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  /**
   * Cross-product selection. Quick Buy swaps the modal to the owner product;
   * the PDP navigates (location.assign so Back returns to the previous product).
   * @param {HTMLInputElement} input
   */
  async function goToLinkedProduct(input) {
    const productUrl = input.dataset.productUrl || '';
    const variantId = input.dataset.variantId || '';
    if (!productUrl) return;

    if (isInQuickBuy(input)) {
      const swap = window.PWQuickAddModal && window.PWQuickAddModal.swapProduct;
      if (typeof swap === 'function') {
        const target = new URL(productUrl, window.location.origin);
        if (variantId) target.searchParams.set('variant', String(variantId));
        try {
          const swapped = await swap(target.toString(), { variantId });
          if (swapped) return;
        } catch (error) {
          console.error('Linked products: Quick Buy swap failed, navigating instead', error);
        }
      }
    }

    window.location.assign(buildTargetUrl(productUrl, variantId));
  }

  /** Restore Superbundle regular mode when a current-product pill is selected. */
  function activateSuperbundleRegular(fromEl) {
    const scope =
      (fromEl && fromEl.closest &&
        fromEl.closest('#quick-add-modal-content, quick-add-dialog, [data-product-grid-content], .shopify-section')) ||
      document;
    const selector = scope.querySelector('.superbundle-selector');
    if (!selector) return;
    const regular = selector.querySelector('input[value="regular"]');
    if (!(regular instanceof HTMLInputElement) || regular.checked) return;
    regular.checked = true;
    if (typeof window.__superbundleSwitchTo === 'function') {
      window.__superbundleSwitchTo('regular', selector);
    }
  }

  /** If a linked pill is the related Superbundle product, switch in place instead of navigating. */
  function activateSuperbundleIfRelated(input) {
    const scope =
      (input && input.closest &&
        input.closest('#quick-add-modal-content, quick-add-dialog, [data-product-grid-content], .shopify-section')) ||
      document;
    const selector = scope.querySelector('.superbundle-selector');
    if (!selector) return false;
    const bundleHandle = selector.dataset.bundleHandle || '';
    const productUrl = input.dataset.productUrl || '';
    if (!bundleHandle || !productUrl || productUrl.indexOf('/products/' + bundleHandle) === -1) {
      return false;
    }
    const bundleRadio = selector.querySelector('input[value="bundle"]');
    if (!(bundleRadio instanceof HTMLInputElement)) return false;
    bundleRadio.checked = true;
    if (typeof window.__superbundleSwitchTo === 'function') {
      window.__superbundleSwitchTo('bundle', selector);
    }
    return true;
  }

  /**
   * Drop unavailable visuals on the clicked pill; other pills in the row keep theirs
   * until the page/modal re-renders.
   * @param {HTMLInputElement} input
   */
  function clearUnavailableLook(input) {
    const label = input.closest('label.variant-option__button-label');
    if (!(label instanceof HTMLElement)) return;
    label.classList.remove('variant-option__button-label--missing');
    label.querySelectorAll(':scope > svg').forEach((svg) => svg.remove());
  }

  /**
   * @param {Event} event
   * @returns {boolean} whether the event was handled
   */
  function handleMainClick(event) {
    if (event.target instanceof Element && event.target.closest('.qa-vpick__opt')) return false;

    const input = getLinkedMainInput(event);
    if (!input) return false;

    if (event instanceof MouseEvent && typeof event.button === 'number' && event.button !== 0) {
      return false;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // Truly missing (no soft target): inert. Soft/unavailable stays clickable.
    if (input.disabled || (input.dataset.state === 'missing' && !input.dataset.variantId)) {
      return true;
    }

    const variantPicker = input.closest('variant-picker');
    const currentProductId =
      variantPicker?.dataset.productId ||
      input.closest('[data-linked-option-row], [data-linked-main-variants]')?.dataset.currentProductId ||
      '';

    const isLinked =
      input.dataset.isLinked === 'true' &&
      String(input.dataset.productId || '') !== String(currentProductId || '');

    syncRowChecked(input);
    clearUnavailableLook(input);
    announce(input);

    if (!isLinked) {
      // If Superbundle is active, return to regular (non-bundle) mode without redirect.
      activateSuperbundleRegular(input);

      const rawOptions = input.dataset.variantOptions;
      if (rawOptions && variantPicker) {
        try {
          const optionValues = JSON.parse(rawOptions);
          if (Array.isArray(optionValues)) {
            driveNativePicker(variantPicker, optionValues.map(String));
          }
        } catch {
          /* ignore malformed attribute */
        }
      }
      return true;
    }

    if (activateSuperbundleIfRelated(input)) return true;

    goToLinkedProduct(input);
    return true;
  }

  /** Surface server-side group warnings (mismatched options, dangling refs) once. */
  function logWarnings() {
    document.querySelectorAll('[data-linked-warnings]').forEach((el) => {
      if (!(el instanceof HTMLElement) || el.dataset.linkedWarningsLogged === 'true') return;
      el.dataset.linkedWarningsLogged = 'true';
      try {
        const warnings = JSON.parse(el.dataset.linkedWarnings || '[]');
        if (Array.isArray(warnings)) {
          warnings.forEach((warning) => console.warn('[linked-products]', warning));
        }
      } catch {
        /* ignore */
      }
    });
  }

  /**
   * @param {Event} event
   */
  function onClick(event) {
    handleMainClick(event);
  }

  function bind() {
    if (!window.__pwLinkedDocBoundV8) {
      window.__pwLinkedDocBoundV8 = true;
      document.addEventListener('click', onClick, true);
    }

    // Also bind on the dialog when present (covers late-opened modal content).
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog && dialog.getAttribute('data-linked-bound') !== 'v7') {
      dialog.setAttribute('data-linked-bound', 'v7');
      dialog.addEventListener('click', onClick, true);
    }

    logWarnings();
  }

  // Expose for quick-add.js to re-bind after modal open.
  window.PWLinkedProducts = {
    bind,
    handleClick: onClick,
  };

  if (!window[FLAG]) {
    window[FLAG] = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }
  }
})();
